/**
 * THE config module for the NDSS Instant Scan (L1).
 *
 * Questions, per-option SPL values, category weights, bands, free-mail and
 * regulated-industry lists and consent wording all live here and nowhere else
 * (build prompt §7, §14).
 *
 * Source of truth for the question set: NDSS_Instant_Scan_Questions.md, itself
 * an extract of Fragenkatalog/NDSS_Standard_v0.4.xlsx. Catalogue IDs verified
 * 2026-07-17.
 *
 * THE INDEX->SPL TRAP: an option's position in `options` is NOT its SPL value.
 * Most questions run 0/1/3/4 — index 2 is SPL 3, not SPL 2. Always map through
 * `splForAnswer()`; never treat the stored answer index as a score.
 */

import type {
  Band,
  Category,
  EmployeeBucketId,
  OptionIndex,
  Question,
  RoleId,
  Spl,
} from './types.js';

/* ------------------------------------------------------------------ *
 * Categories and weights
 * ------------------------------------------------------------------ */

/**
 * Recalibrated demand-side weights (concept §2.1). The Categories sheet in
 * NDSS_Standard_v0.4.xlsx is STALE — these values govern. Do not "fix" them
 * against the spreadsheet.
 */
export const CATEGORIES: readonly Category[] = [
  {
    id: 1,
    code: 'CAT-1',
    catalogueTotal: 7,
    shortTitle: 'Governance',
    themeColor: '#0072BC',
    name: 'Governance & Strategic Control',
    weight: 15,
    prompt: 'Is there a named owner for sovereignty decisions?',
  },
  {
    id: 2,
    code: 'CAT-2',
    catalogueTotal: 10,
    shortTitle: 'Jurisdiction',
    themeColor: '#005B96',
    name: 'Jurisdictional Resilience',
    weight: 15,
    prompt: 'Could a non-EU authority reach your critical data?',
  },
  {
    id: 3,
    code: 'CAT-3',
    catalogueTotal: 14,
    shortTitle: 'Data & AI',
    themeColor: '#00DFED',
    name: 'Data & AI Autonomy',
    weight: 10,
    prompt: "Does your data train someone else's model?",
  },
  {
    id: 4,
    code: 'CAT-4',
    catalogueTotal: 12,
    shortTitle: 'Operations',
    themeColor: '#19A3FC',
    name: 'Operational Independence',
    weight: 15,
    prompt: 'Could you run critical services without the vendor?',
  },
  {
    id: 5,
    code: 'CAT-5',
    catalogueTotal: 13,
    shortTitle: 'Value Chain',
    themeColor: '#070F26',
    name: 'Value Chain Integrity',
    weight: 10,
    prompt: 'Do you know the origin of your critical dependencies?',
  },
  {
    id: 6,
    code: 'CAT-6',
    catalogueTotal: 10,
    shortTitle: 'Self-Determ.',
    themeColor: '#2E404D',
    name: 'Technological Self-Determination',
    weight: 20,
    prompt: 'Could you swap the AI model behind an app?',
  },
  {
    id: 7,
    code: 'CAT-7',
    catalogueTotal: 8,
    shortTitle: 'Trust',
    themeColor: '#7CC4F1',
    name: 'Trust, Security & Assurance',
    weight: 10,
    prompt: 'Is control independently attested, not just claimed?',
  },
  {
    id: 8,
    code: 'CAT-8',
    catalogueTotal: 6,
    shortTitle: 'Footprint',
    themeColor: '#3F4B66',
    name: 'Sustainable Digital Footprint',
    weight: 5,
    prompt: 'Is sovereignty balanced against energy cost?',
  },
] as const;

/** Startup assertion required by build prompt §7. */
export function assertWeightsSumTo100(): void {
  const sum = CATEGORIES.reduce((acc, c) => acc + c.weight, 0);
  if (sum !== 100) {
    throw new Error(
      `NDSS config invariant violated: category weights sum to ${sum}, expected 100.`,
    );
  }
}

/* ------------------------------------------------------------------ *
 * Bands
 * ------------------------------------------------------------------ */

export const BANDS: readonly Band[] = [
  {
    id: 'foundation',
    label: 'Foundation',
    min: 0,
    max: 25,
    interpretation:
      'An early position with broad exposure across most dimensions — a starting point, not a verdict.',
  },
  {
    id: 'developing',
    label: 'Developing',
    min: 26,
    max: 50,
    interpretation:
      'A workable base with real, addressable exposure — a starting position, not a verdict.',
  },
  {
    id: 'mature',
    label: 'Mature',
    min: 51,
    max: 75,
    interpretation:
      'Sound control in most dimensions, with specific areas that still carry real exposure.',
  },
  {
    id: 'leading',
    label: 'Leading',
    min: 76,
    max: 100,
    interpretation:
      'Strong control across the board — the remaining gaps are narrow and specific.',
  },
] as const;

/* ------------------------------------------------------------------ *
 * The 12 questions
 * ------------------------------------------------------------------ */

export const QUESTIONS: readonly Question[] = [
  {
    id: 1,
    catalogueId: '1.1',
    categoryId: 1,
    text: 'Who really decides your critical technology, data, AI and cloud-provider choices?',
    options: [
      { label: 'Our providers or group HQ effectively make those choices.', spl: 0 },
      { label: 'We can co-decide, but core choices follow external roadmaps.', spl: 1 },
      { label: 'We hold our own decision rights and apply them to critical choices.', spl: 3 },
      {
        label: 'We steer critical capabilities fully autonomously, independent of any provider.',
        spl: 4,
      },
    ],
  },
  {
    id: 2,
    catalogueId: '1.4',
    categoryId: 1,
    text: 'Is digital sovereignty owned by someone accountable, with a real mandate and KPIs?',
    options: [
      { label: 'No one clearly owns it; roles and mandate are missing.', spl: 0 },
      { label: "Someone owns it for critical areas, but there's no board-level reporting.", spl: 2 },
      { label: 'Clear governance with board reporting and defined KPIs.', spl: 3 },
      { label: 'Board-accountable owner, RACI, and an externally validated framework.', spl: 4 },
    ],
  },
  {
    id: 3,
    catalogueId: '2.2',
    categoryId: 2,
    text: 'Could a non-EU authority legally reach your critical data today (e.g. US CLOUD Act), and have you mitigated it?',
    options: [
      {
        label: 'High exposure; critical systems are reachable and unmitigated, or never assessed.',
        spl: 0,
      },
      { label: 'We know the risk, but have no technical or contractual mitigation.', spl: 1 },
      {
        label: 'Largely mitigated: EU alternatives or customer-held keys for most critical data.',
        spl: 3,
      },
      { label: 'No residual non-EU access path, and it has been independently validated.', spl: 4 },
    ],
  },
  {
    id: 4,
    catalogueId: '2.3',
    categoryId: 2,
    // Tops out at SPL 3 in the official catalogue. This is by design, not an
    // omission — do not "correct" it to 4 (build prompt §7).
    text: 'If a non-EU authority demanded your data, would you be told, and could you refuse or narrow the request?',
    options: [
      { label: 'No process; a request would be answered without us even knowing.', spl: 0 },
      { label: "We're aware of the duty, but nothing is formalised.", spl: 1 },
      {
        label: 'Notification duty and an escalation process are defined for critical providers.',
        spl: 2,
      },
      {
        label: "Notification is in our critical contracts, with a refuse/scope process we've used.",
        spl: 3,
      },
    ],
  },
  {
    id: 5,
    catalogueId: '3.13',
    categoryId: 3,
    text: 'Is your data blocked, contractually and technically, from being used to train external AI models?',
    options: [
      { label: 'No control; our data could be used for AI training uncontrolled.', spl: 0 },
      { label: "We haven't fully checked the terms; the risk is unclear.", spl: 1 },
      { label: 'Excluded both contractually and technically (AI gateway, DLP, logging).', spl: 3 },
      { label: 'Fully isolated; no-training is proven and tenant isolation is audited.', spl: 4 },
    ],
  },
  {
    id: 6,
    catalogueId: '4.1',
    categoryId: 4,
    text: 'If your main provider had an outage or withdrew support, could you keep a critical system running?',
    options: [
      { label: "Critical operation fully depends on the provider; we can't self-run.", spl: 0 },
      { label: 'Self-operation is theoretical; every fault needs external help.', spl: 1 },
      { label: 'We can self-run most critical systems, with exceptions documented.', spl: 3 },
      {
        label: 'Full controlled operation through outage and support-withdrawal, tested in drills.',
        spl: 4,
      },
    ],
  },
  {
    id: 7,
    catalogueId: '4.3',
    categoryId: 4,
    text: 'Do you have a tested exit plan off your main provider, with an EU alternative ready?',
    options: [
      { label: 'No exit strategy; a switch would be unprepared.', spl: 0 },
      { label: 'We know we should, but nothing is documented or tested.', spl: 1 },
      {
        label: 'Documented exit strategies for all critical systems, EU alternatives identified.',
        spl: 3,
      },
      { label: 'Exit tested regularly, alternative pre-contracted, EU-first.', spl: 4 },
    ],
  },
  {
    id: 8,
    catalogueId: '5.1',
    categoryId: 5,
    text: 'Do you know your critical suppliers and their sub-suppliers, down to software and firmware origin?',
    options: [
      { label: "We don't systematically know our critical chains and sub-suppliers.", spl: 0 },
      {
        label: 'We know the main suppliers, but sub-suppliers, firmware, OSS and AI parts are unclear.',
        spl: 1,
      },
      { label: 'Supply-chain data is risk-based and maintained, with SBOMs for critical systems.', spl: 3 },
      { label: 'End-to-end transparency incl. sub-suppliers, with audit rights and monitoring.', spl: 4 },
    ],
  },
  {
    id: 9,
    catalogueId: '6.1',
    categoryId: 6,
    text: 'How realistically could you move a critical workload to another platform, in cost, time and risk?',
    options: [
      { label: 'Proprietary interfaces and formats; realistic portability is absent.', spl: 0 },
      { label: 'Migration is theoretically possible, but very high effort and never done.', spl: 1 },
      { label: 'Exit-by-design is an architecture principle we actively check.', spl: 3 },
      { label: 'Provably transferable; we run exit tests regularly.', spl: 4 },
    ],
  },
  {
    id: 10,
    catalogueId: '6.3',
    categoryId: 6,
    text: 'Could you swap the AI model or agent behind an application without re-engineering it?',
    options: [
      { label: "Hard-wired to one AI model/agent provider; substitution isn't possible.", spl: 0 },
      { label: 'Substitution is conceivable but needs major re-engineering.', spl: 1 },
      { label: 'Model access is abstracted; substitution tested for critical use.', spl: 3 },
      { label: "Full model portability; we've swapped providers without re-engineering.", spl: 4 },
    ],
  },
  {
    id: 11,
    catalogueId: '7.6',
    categoryId: 7,
    text: 'Can you show, with evidence and current certifications, that you meet your sovereignty-relevant assurance requirements?',
    options: [
      { label: "Requirements aren't known or implemented; no assurance.", spl: 0 },
      { label: 'Gap analysis done, implementation not begun.', spl: 1 },
      { label: 'Largely implemented; independent certifications current, evidence maintained.', spl: 3 },
      { label: 'Comprehensive independent assurance, continuously evidenced and review-ready.', spl: 4 },
    ],
  },
  {
    id: 12,
    catalogueId: '8.2',
    categoryId: 8,
    text: 'Is your IT, own and cloud, powered predominantly by renewable or low-carbon energy?',
    options: [
      { label: 'No focus on renewables for our IT.', spl: 0 },
      { label: 'Discussed, but no binding plan or monitoring.', spl: 1 },
      { label: 'Predominantly renewable, with an active roadmap to a full switch.', spl: 3 },
      { label: '100% renewable, PPAs/RECs in place, externally verified.', spl: 4 },
    ],
  },
] as const;

/**
 * Map a stored answer (option index 0-3) to the SPL it records.
 * This is the ONLY sanctioned way to turn an answer into a score input.
 */
export function splForAnswer(questionId: number, optionIndex: OptionIndex): Spl {
  const q = QUESTIONS.find((question) => question.id === questionId);
  if (!q) throw new Error(`Unknown question id ${questionId}`);
  const option = q.options[optionIndex];
  if (!option) throw new Error(`Question ${questionId} has no option at index ${optionIndex}`);
  return option.spl;
}

/* ------------------------------------------------------------------ *
 * Evidence model (E1-E4 x C1-C2). Never an invented "x/10" number.
 * ------------------------------------------------------------------ */

export const EVIDENCE_LADDER = [
  {
    tier: 'Instant Scan',
    shortTier: 'Instant',
    floor: 'E1 / C1',
    detail: 'self-declared, one respondent',
    state: 'here' as const,
  },
  {
    tier: 'Quick Scan',
    shortTier: 'Quick',
    floor: 'E1 / C1',
    detail: 'interview-based',
    state: null,
  },
  {
    tier: 'Deep Scan',
    shortTier: 'Deep',
    floor: 'E2 or C2',
    detail: 'document-backed',
    state: 'next' as const,
  },
  {
    tier: 'Complete Scan',
    shortTier: 'Complete',
    floor: 'E2 or C2',
    detail: 'corroborated + technical',
    state: null,
  },
] as const;

/* ------------------------------------------------------------------ *
 * Lead-form vocabularies
 * ------------------------------------------------------------------ */

export const ROLES: readonly { id: RoleId; label: string }[] = [
  { id: 'c-level', label: 'C-level (CIO / CTO / CDO / CISO)' },
  { id: 'compliance-legal', label: 'Compliance / Legal' },
  { id: 'architect', label: 'Architect' },
  { id: 'other', label: 'Other' },
] as const;

export const EMPLOYEE_BUCKETS: readonly {
  id: EmployeeBucketId;
  label: string;
  /** Lower bound, used for tiering. */
  min: number;
  /** Upper bound; Infinity for the open-ended top bucket. */
  max: number;
}[] = [
  { id: '1-99', label: '1–99', min: 1, max: 99 },
  { id: '100-499', label: '100–499', min: 100, max: 499 },
  { id: '500-999', label: '500–999', min: 500, max: 999 },
  { id: '1000-4999', label: '1,000–4,999', min: 1000, max: 4999 },
  { id: '5000+', label: '5,000+', min: 5000, max: Number.POSITIVE_INFINITY },
] as const;

/**
 * NAICS-aligned industry list. `regulated` drives Tier-A qualification and is a
 * commercial judgement (financial services, health, public sector and the NIS2
 * essential-entity sectors), not a legal classification.
 */
export const INDUSTRIES: readonly { id: string; label: string; regulated: boolean }[] = [
  { id: 'banking', label: 'Banking & capital markets', regulated: true },
  { id: 'insurance', label: 'Insurance', regulated: true },
  { id: 'healthcare', label: 'Healthcare providers', regulated: true },
  { id: 'pharma', label: 'Pharmaceuticals & life sciences', regulated: true },
  { id: 'public-sector', label: 'Public sector & government', regulated: true },
  { id: 'energy', label: 'Energy & utilities', regulated: true },
  { id: 'telecom', label: 'Telecommunications', regulated: true },
  { id: 'transport', label: 'Transport & logistics', regulated: true },
  { id: 'defence', label: 'Defence & aerospace', regulated: true },
  { id: 'manufacturing', label: 'Manufacturing & industrials', regulated: false },
  { id: 'automotive', label: 'Automotive', regulated: false },
  { id: 'retail', label: 'Retail & consumer goods', regulated: false },
  { id: 'technology', label: 'Technology & software', regulated: false },
  { id: 'professional-services', label: 'Professional services', regulated: false },
  { id: 'media', label: 'Media & entertainment', regulated: false },
  { id: 'education', label: 'Education & research', regulated: false },
  { id: 'other', label: 'Other', regulated: false },
] as const;

/**
 * Free-mail domains rejected at the gate. Editable list (build prompt §8).
 * Matching is on the exact registrable domain, case-insensitive.
 */
export const FREE_MAIL_DOMAINS: readonly string[] = [
  'gmail.com',
  'googlemail.com',
  'outlook.com',
  'outlook.de',
  'hotmail.com',
  'hotmail.de',
  'live.com',
  'live.de',
  'yahoo.com',
  'yahoo.de',
  'gmx.de',
  'gmx.net',
  'gmx.com',
  'web.de',
  'proton.me',
  'protonmail.com',
  'icloud.com',
  'me.com',
  'aol.com',
  'mail.com',
  't-online.de',
  'freenet.de',
] as const;

export const FREE_MAIL_ERROR = 'Please use your business email, not a free-mail address.';

/* ------------------------------------------------------------------ *
 * Consent wording — versioned. Bump the version whenever the text changes;
 * the version shown to the visitor is persisted with their decision so a
 * consent record can always be traced back to exact wording (build prompt §8).
 * ------------------------------------------------------------------ */

export const CONSENT = {
  controller: 'NTT DATA Deutschland SE',
  privacyNoticeUrl: 'https://www.nttdata.com/global/en/privacy-policy',
  withdrawalContact: 'privacy@nttdata.com',
  processing: {
    wordingVersion: 'processing-v1-2026-07-18',
    text:
      'By submitting, you agree that NTT DATA Deutschland SE processes the details above ' +
      'to generate and provide the NDSS Scorecard. See our privacy notice.',
  },
  marketing: {
    wordingVersion: 'marketing-v1-2026-07-18',
    text:
      'Yes, NTT DATA may contact me about the Full-Stack Sovereignty Scan and related services.',
  },
  retentionMicrocopy:
    'We keep your details for 12 months unless you ask us to delete them. ' +
    'You can withdraw consent at any time at privacy@nttdata.com.',
} as const;

/* ------------------------------------------------------------------ *
 * Contact / offering constants used in copy and in the Scorecard
 * ------------------------------------------------------------------ */

export const OFFERING = {
  name: 'Full-Stack Sovereignty Scan',
  tier: 'Instant Scan',
  deliverable: 'NDSS Scorecard',
  contactEmail: 'sovereignty@nttdata.com',
  landingUrl: 'nttdata.com/sovereignty-scan',
} as const;

/** Estimated seconds per question, used for the "~N min left" progress label. */
export const SECONDS_PER_QUESTION = 35;
